import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { RefreshCw } from "lucide-react";
import { ShareConsentCard } from "@/components/llm_network/ShareConsentCard";
import { ProviderRegistryTable } from "@/components/llm_network/ProviderRegistryTable";
import { NetworkUsagePanel } from "@/components/llm_network/NetworkUsagePanel";
import { NetworkTestCard } from "@/components/llm_network/NetworkTestCard";
import { NetworkCatalogCard } from "@/components/llm_network/NetworkCatalogCard";
import { EnvRegionsCard } from "@/components/llm_network/EnvRegionsCard";
import { SummaryWarmupCard } from "@/components/llm_network/SummaryWarmupCard";

/** Settings → Shared LLM Providers. Hub: registry + routing policy + usage. Every deployment: owner consent + what the network offers. */
export default function SharedLlmProvidersPage() {
    const { user } = useAuth();
    const canWrite = user?.role === "super_admin";
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);

    const load = useCallback(async () => {
        try {
            const r = await api.get("/admin/llm-network/overview");
            setData(r.data); setErr(null);
        } catch (e) {
            setErr(formatApiErrorDetail(e?.response?.data?.detail));
        }
    }, []);

    useEffect(() => {
        load();
        const id = setInterval(load, 30000);
        return () => clearInterval(id);
    }, [load]);

    const isHub = data?.mode === "hub";
    return (
        <div className="px-4 sm:px-8 py-8 max-w-6xl" data-testid="shared-llm-page">
            <div className="mb-8 flex flex-wrap items-start justify-between gap-4">
                <div>
                    <div className="label-xs text-[#7C3AED]">Configuration</div>
                    <h1 className="font-display text-4xl tracking-tight mt-2">Shared LLM Providers</h1>
                    <p className="text-sm text-white/50 mt-2 max-w-2xl">
                        {isHub
                            ? "Every environment of the network advertises the LLM credentials it holds (capabilities and a fingerprint — never the key). You decide here which of them the network may use, for which workloads and environments, with which priority, budget and data-residency rule. Requests are coordinated by this hub and executed by the owning environment; results are cached and deduplicated network-wide."
                            : "AI features on this environment can run on credentials shared elsewhere in the network — coordinated by the hub, executed by the owner, never a key in transit. Below you authorise sharing your own credentials and see what the hub lets this environment use."}
                    </p>
                    {data && (
                        <div className="mt-3 flex flex-wrap gap-2 text-[11px] text-white/50">
                            <span className="rounded border border-white/10 px-2 py-0.5" data-testid="shared-llm-mode-pill">mode <span className="text-white/90">{data.mode}</span></span>
                            <span className="rounded border border-white/10 px-2 py-0.5" data-testid="shared-llm-env-pill">{data.env_name}</span>
                            <span className="rounded border border-white/10 px-2 py-0.5" data-testid="shared-llm-region-pill">region <span className="text-white/90">{data.region || "unset"}</span></span>
                            {!data.region && <span className="rounded border border-amber-400/30 px-2 py-0.5 text-amber-200" data-testid="shared-llm-region-hint">set RADAR_ENV_REGION ({(data.known_regions || ["eu", "us", "ca", "uk"]).join(" / ")}) so tenant workloads can use same-region credentials</span>}
                        </div>
                    )}
                </div>
                <button onClick={load} className="inline-flex items-center gap-1.5 rounded border border-white/10 px-3 py-1.5 text-xs text-white/70 hover:bg-white/5" data-testid="shared-llm-refresh-btn">
                    <RefreshCw className="w-3.5 h-3.5" /> Refresh
                </button>
            </div>

            {err && <div className="text-xs text-rose-300 border border-rose-500/30 bg-rose-500/10 rounded px-3 py-2 mb-4" data-testid="shared-llm-error">{err}</div>}
            {!data && !err && <div className="text-xs text-white/40 font-mono" data-testid="shared-llm-loading">Loading…</div>}

            {data && (
                <div className="space-y-4">
                    <ShareConsentCard share={data.share} capabilities={data.capabilities} canWrite={canWrite} isHub={isHub} onChanged={load} />
                    {isHub && <ProviderRegistryTable rows={data.providers} workloads={data.workloads} environments={data.environments} canWrite={canWrite} onChanged={load} />}
                    {isHub && <EnvRegionsCard environments={data.environments} hubRegion={data.region} knownRegions={data.known_regions} canWrite={canWrite} onChanged={load} />}
                    {!isHub && <NetworkCatalogCard catalog={data.catalog} hasHubLink={data.has_hub_link} />}
                    <SummaryWarmupCard canWrite={canWrite} />
                    <NetworkTestCard />
                    <NetworkUsagePanel usage={data.usage} cache={data.cache} isHub={isHub} />
                    {!canWrite && <div className="text-[11px] text-white/40" data-testid="shared-llm-readonly-note">Sharing consent, routing policy and regions require a super admin.</div>}
                </div>
            )}
        </div>
    );
}
